// src/components/UserSelect.tsx

import React from 'react'
import { fetchUsers } from '@/api/userApi'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import { InfiniteSelect, type SelectOption } from './InfiniteSelect'
import type { ApiUserType } from '@/schemas/user.schema'

export interface UserSelectValue {
  id: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
}

interface UserSelectProps {
  value?: UserSelectValue | UserSelectValue[] | null
  onValueChange: (user: UserSelectValue | UserSelectValue[] | null) => void
  placeholder?: string
  label?: string
  required?: boolean
  className?: string
  error?: string
  disabled?: boolean
  roleFilter?: string[]
  statusFilter?: string[]
  multiple?: boolean
  maxSelections?: number
  disabledOptions?: string[]
}

export function UserSelect({
  value,
  onValueChange,
  placeholder = 'Select user...',
  label = 'User',
  required = false,
  className = '',
  error,
  disabled = false,
  roleFilter,
  statusFilter,
  multiple = false,
  maxSelections,
  disabledOptions = [],
}: Readonly<UserSelectProps>) {
  const transformUser = (user: UserSelectValue): SelectOption => ({
    id: user.id,
    label: `${user.firstName} ${user.lastName}`.trim(),
    value: user.id,
    subtitle: user.email,
    userData: user,
  })

  const selectedOptions = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value.map(transformUser) : []
    } else {
      return value && !Array.isArray(value) ? transformUser(value) : null
    }
  }, [value, multiple])

  const handleValueChange = (options: SelectOption | SelectOption[] | null) => {
    if (!options) {
      onValueChange(null)
      return
    }

    if (multiple && Array.isArray(options)) {
      const users: UserSelectValue[] = options
        .map(option => option.userData as UserSelectValue)
        .filter(Boolean)

      onValueChange(users.length > 0 ? users : null)
    } else if (!multiple && !Array.isArray(options)) {
      const option = options

      if (option.userData && typeof option.userData === 'object') {
        onValueChange(option.userData as UserSelectValue)
      } else {
        const [firstName, ...lastNameParts] = option.label.split(' ')
        const reconstructedUser: UserSelectValue = {
          id: option.id,
          firstName: firstName || '',
          lastName: lastNameParts.join(' ') || '',
          email: option.subtitle || '',
          phoneNumber: '',
        }
        onValueChange(reconstructedUser)
      }
    }
  }

  const additionalParams = React.useMemo(() => {
    const params: Record<string, unknown> = {}

    if (roleFilter && roleFilter.length > 0) {
      params.role = roleFilter
    }

    if (statusFilter && statusFilter.length > 0) {
      params.status = statusFilter
    }

    return params
  }, [roleFilter, statusFilter])

  const resolvedPlaceholder =
    multiple && placeholder === 'Select user...'
      ? 'Select users...'
      : placeholder

  return (
    <InfiniteSelect<ApiUserType>
      queryKey={USER_QUERY_KEYS.all}
      queryFn={fetchUsers}
      transformData={transformUser}
      value={selectedOptions}
      onValueChange={handleValueChange}
      placeholder={resolvedPlaceholder}
      label={label}
      required={required}
      className={className}
      error={error}
      disabled={disabled}
      searchPlaceholder="Search users..."
      emptyMessage="No users available"
      noResultsMessage="No users found"
      pageSize={20}
      additionalParams={additionalParams}
      enableSearch={true}
      showAvatar={false}
      multiple={multiple}
      maxSelections={maxSelections}
      disabledOptions={disabledOptions}
    />
  )
}
